/**
 * Deux decisions d'acces, isolees du reseau et de React pour etre testables.
 *
 * Elles portent la meme regle que le correctif serveur D.1320 : en l'absence
 * d'information, on ne DEVINE pas. Un defaut accorde en silence est un defaut
 * qui joue dans les deux sens — il refuse un admin, et il accorderait un
 * niveau a qui n'en a aucun.
 */

/**
 * Points d'entree dont le METIER est de verifier des identifiants.
 *
 * ⚠️ D.1306 — SUR CES CHEMINS, UN 401 NE VEUT PAS DIRE « SESSION MORTE ».
 * Il veut dire « ces identifiants-la sont faux ». Detruire la session ici,
 * c'est ejecter vers /login quelqu'un qui vient de taper un mauvais mot de
 * passe — y compris depuis /settings, la page ou il allait le changer.
 *
 * ⚠️ La liste est MESUREE, pas devinee : relevee sur `api/routes/auth.py`
 * (8 routes) croisee avec les appels du frontend. Les autres 401 — dont
 * `/auth/refresh` et `/faceai/search` — signifient bien une session morte et
 * DOIVENT continuer a deconnecter. Ne pas elargir cette liste sans preuve :
 * chaque ajout est un endroit ou une session morte cesse d'etre detectee.
 */
export const CREDENTIAL_PATHS = [
  '/auth/login',
  '/auth/face-login',
  '/auth/reset-password',
] as const;

/** Retire la chaine de requete et le fragment : on ne compare que le chemin. */
function pathOnly(url: string | undefined | null): string {
  if (!url) return '';
  return url.split('?')[0].split('#')[0];
}

/**
 * Un 401 sur cette requete doit-il detruire la session ?
 *
 * ⚠️ La comparaison se fait sur la FIN du chemin, avec la barre initiale du
 * motif. `baseURL` prefixe les URL (`/api/v1`), donc une egalite stricte ne
 * reconnaitrait rien. Et garder la barre evite les sous-chaines accidentelles :
 * `/auth/login-history` ne finit pas par `/auth/login`, `/xauth/login` non plus.
 */
export function shouldDestroySessionOn401(url: string | undefined | null): boolean {
  const path = pathOnly(url);
  if (!path) return true; // URL inconnue : on retombe sur le cas sur (deconnecter)
  return !CREDENTIAL_PATHS.some((candidate) => path === candidate || path.endsWith(candidate));
}

/**
 * Trois etats, pas deux.
 *
 * ⚠️ D.1308 — `unknownRole` N'EST PAS `denied`. Le store ne persiste que
 * `theme`, `sidebar` et `graphViewMode` : au rechargement, le role est absent
 * le temps que `/auth/me` reponde. Le confondre avec un refus affiche
 * « Acces refuse » a un admin authentifie — et comme `useCurrentUser` porte
 * `retry: false`, un seul echec de `/auth/me` rendait ce refus DEFINITIF.
 */
export type AccessVerdict = 'granted' | 'denied' | 'unknownRole';

export function accessVerdict(
  role: string | null | undefined,
  requiredRoles: readonly string[] | undefined,
): AccessVerdict {
  // Aucune exigence de role : la seule condition est d'etre authentifie.
  if (!requiredRoles || requiredRoles.length === 0) return 'granted';
  // Role absent ou vide : on ne sait pas encore. On ne refuse PAS, on le dit.
  if (!role) return 'unknownRole';
  return requiredRoles.includes(role) ? 'granted' : 'denied';
}

/**
 * Pourquoi le role n'est-il pas connu ?
 *
 * ⚠️ Trois raisons, et elles ne se disent pas de la meme facon a l'ecran.
 * Depuis le correctif serveur D.1320, `get_current_user` repond 403 a un jeton
 * qui ne porte aucun role Helios exploitable — ce qui EST un refus, et non une
 * panne. Les confondre afficherait « ce n'est pas un refus d'acces » a qui
 * vient precisement d'en recevoir un.
 */
export type RoleResolution = 'pending' | 'serverRefused' | 'unresolved';

export function roleResolution(opts: {
  resolving: boolean;
  failed: boolean;
  status?: number;
}): RoleResolution {
  // Un refus du serveur est une REPONSE : il tranche, meme si une requete est
  // encore en vol. On le regarde donc avant l'etat « en cours ».
  if (opts.status === 401 || opts.status === 403) return 'serverRefused';
  if (opts.resolving) return 'pending';
  if (opts.failed) return 'unresolved';
  // Ni en cours, ni en echec, et toujours pas de role : on ne sait pas
  // pourquoi. C'est « non resolu », pas « accorde ».
  return 'unresolved';
}

/**
 * Ce corps de requete doit-il laisser le NAVIGATEUR poser son Content-Type ?
 *
 * ⚠️⚠️ D.129 — 21 APPELS ECRIVAIENT `multipart/form-data` A LA MAIN, donc
 * SANS `boundary`. Or la frontiere est tiree par le navigateur a l'envoi, et
 * elle n'est ajoutee QUE s'il pose l'en-tete lui-meme. Mesure du 09/10/2026
 * dans `node_modules/axios/lib/adapters/xhr.js` (axios 1.13.6) : **zero** cas
 * particulier `FormData`, et les en-tetes sont poses verbatim
 * (`request.setRequestHeader(key, val)` pour chacun). L'en-tete ecrit a la
 * main part donc tel quel, sans frontiere, et le serveur ne peut pas
 * segmenter le corps.
 *
 * ⚠️⚠️ ET LE CORRECTIF EVIDENT EST UN PIEGE PIRE QUE LE DEFAUT. Supprimer
 * l'en-tete de l'appel laisserait s'appliquer le defaut du client
 * (`Content-Type: application/json`, `api/client.ts`), et
 * `axios/lib/defaults/index.js:53` dit alors :
 *     if (isFormData) return hasJSONContentType
 *         ? JSON.stringify(formDataToJSON(data)) : data;
 * — le fichier serait donc converti en JSON et PERDU. Il faut RETIRER
 * l'en-tete, pas le remplacer.
 *
 * ⚠️ Corrige en UN endroit (l'intercepteur de requete) et non sur les 21
 * appels : un correctif pose a 21 endroits laisse le 22ᵉ, qui s'ecrira demain.
 */
export function shouldLetBrowserSetContentType(data: unknown): boolean {
  if (typeof FormData !== 'undefined' && data instanceof FormData) return true;
  // `Blob` et `File` portent leur propre type MIME : le navigateur sait aussi.
  if (typeof Blob !== 'undefined' && data instanceof Blob) return true;
  return false;
}
